import { useContext, useRef, useState } from 'react'

import Cookie from 'js-cookie'
import { func } from 'prop-types'
import { notification } from 'antd'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'

import LoginForm from '../../../Components/anonymous/LoginForm'
import ProfileSelection from '../../../Components/anonymous/ProfileSelection'
import AnonymousFormWrapper from '../../../Components/shared/AnonymousFormWrapper'
import { login } from '../../../utils/api/auth'
import { Routes } from '../../../routes'
import { HttpStatuses } from '../../../utils/http'
import NonProdEnvironmentBanner from '../../../Components/shared/NonProdEnvironmentBanner'
import { RequestPortalContext } from '../../../Providers/RequestPortalProvider'
import { StorageKeys } from '../../../Providers/AuthProvider'
import { Cookies } from '../../../utils/Cookie'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'

const LoginView = props => {
  const { studentEmail } = useContext(RequestPortalContext)

  const t = useSelector(selectTranslate)

  const [loginPending, setLoginPending] = useState(false)
  const [profiles, setProfiles] = useState(null)
  // POST /auth is stateless: in a two-step login, step 2 has to send the credentials again along
  // with the chosen profile. They are kept in memory (never persisted) during the selection.
  const credentialsRef = useRef(null)
  const history = useHistory()

  const authenticate = async ({ username, password, remember, profileId = null }) => {
    setLoginPending(true)

    try {
      const response = await login(username.trim(), password, profileId)

      if (response && Array.isArray(response.profiles)) {
        // Step 1: the account is authenticated but has zero or several active profiles.
        credentialsRef.current = { username, password, remember }
        setProfiles(response.profiles)
        setLoginPending(false)
        return
      }

      const token = response?.token

      if (typeof token === 'undefined') {
        setLoginPending(false)
        return
      }

      if (remember) {
        Cookie.set(Cookies.TOKEN, JSON.stringify(token), { expires: 7 })
      } else {
        Cookie.remove(Cookies.TOKEN)
      }

      // Local session: purge any leftover Opal account_id (left by an earlier SSO login on this
      // browser) — otherwise logging out would redirect this local account to the Opal logout with a
      // stale account_id, possibly someone else's.
      window.localStorage.removeItem(StorageKeys.ACCOUNT_ID)

      credentialsRef.current = null

      props.onGetUserContextAndData(token).then(() => {
        if (typeof props.onAfterLogin === 'function') {
          props.onAfterLogin()
        }
      })
      setLoginPending(false)
    } catch (err) {
      setLoginPending(false)

      // A 403 at step 2 means PROFILE_NOT_LINKED_TO_ACCOUNT: the chosen profile no longer exists
      // (deleted between the list being displayed and the click). Report it and refresh the list by
      // running the auth again WITHOUT a profile (same credentials) — the backend returns the
      // up-to-date list, or a token when a single profile is left.
      if (err?.status === HttpStatuses.FORBIDDEN) {
        notification.error({ message: t('login_form.profile_unavailable'), placement: 'bottomRight' })

        if (credentialsRef.current) {
          authenticate({ ...credentialsRef.current, profileId: null })
        } else {
          setProfiles(null)
        }

        return
      }

      Cookie.remove(Cookies.TOKEN)

      notification.error({
        message: err?.status === HttpStatuses.UNAUTHORIZED
          ? t('login_form.invalid_credentials')
          : t('error_handler.unknown_error'),
        placement: 'bottomRight'
      })
    }
  }

  return (
    <AnonymousFormWrapper topBanner={<NonProdEnvironmentBanner />}>
      {profiles === null && (
        <LoginForm
          loginPending={loginPending}
          prefilledEmail={studentEmail}
          onLogin={authenticate}
          onPasswordResetClick={() => {
            history.push(Routes.PASSWORD_RESET_REQUEST)
          }}
        />
      )}
      {profiles !== null && (
        <ProfileSelection
          profiles={profiles}
          loading={loginPending}
          onSelect={profileId => authenticate({ ...credentialsRef.current, profileId })}
          onBack={() => {
            credentialsRef.current = null
            setProfiles(null)
          }}
        />
      )}
    </AnonymousFormWrapper>
  )
}

LoginView.propTypes = {
  onGetUserContextAndData: func.isRequired
}

export default LoginView
